
// 🛡️ REUSABLE ERROR HANDLING UTILITIES
// These functions can be used by any component to handle errors gracefully

// Universal error logging with detailed context (Moved from App.js)
import { logger } from './logger';

export const logModuleError = (moduleName, errorType, error, additionalInfo = {}) => {
    console.group(`🚨 ${moduleName.toUpperCase()}: ${errorType}`);
    console.error('Error:', error);
    console.error('Error Type:', errorType);
    console.error('Module:', moduleName);
    console.error('Additional Info:', additionalInfo);
    console.error('Timestamp:', new Date().toISOString());
    console.error('User Agent:', navigator.userAgent);
    console.error('URL:', window.location.href);
    console.groupEnd();
};

// 🔄 NAVIGATION HANDLER (updated for new structure with correct field naming)
// Moved from App.js
export const createNavigationHandler = (navigate, level, chapter, moduleName, modulesOrder = null, moduleMap = null) => {
    const moduleSequence = modulesOrder || ['chapterTitle', 'dialogue', 'vocabulary', 'translation', 'grammar', 'cloze', 'shapePuzzle', 'complete'];

    return {
        next: () => {
            try {
                const currentIndex = moduleSequence.indexOf(moduleName);
                const nextModule = moduleSequence[currentIndex + 1];
                if (nextModule) {
                    const routePath = moduleMap?.[nextModule]?.routePath || nextModule;
                    logger.log('Navigation', `Navigating Next from ${moduleName} to ${routePath}`);
                    navigate(`/level/${level}/chapter/${chapter}/${routePath}`);
                } else {
                    logger.log('Navigation', `Navigating Next from ${moduleName} to Complete`);
                    navigate(`/level/${level}/chapter/${chapter}/complete`);
                }
            } catch (error) {
                logger.error('Navigation', `Next navigation failed for ${moduleName}`, error);
                logModuleError(moduleName, 'Navigation Error', error, { action: 'next' });
                navigate(`/level/${level}`);
            }
        },
        previous: () => {
            try {
                const currentIndex = moduleSequence.indexOf(moduleName);
                const prevModule = moduleSequence[currentIndex - 1];
                logger.log('Navigation', `Navigating Previous from ${moduleName}`);
                if (prevModule) {
                    const routePath = moduleMap?.[prevModule]?.routePath || prevModule;
                    navigate(`/level/${level}/chapter/${chapter}/${routePath}`);
                } else {
                    navigate(`/level/${level}`);
                }
            } catch (error) {
                logger.error('Navigation', `Previous navigation failed for ${moduleName}`, error);
                logModuleError(moduleName, 'Navigation Error', error, { action: 'previous' });
                navigate(`/level/${level}`);
            }
        },
        menu: () => {
            try {
                logger.log('Navigation', `Navigating Menu from ${moduleName}`);
                navigate('/map');
            } catch (error) {
                logger.error('Navigation', `Menu navigation failed for ${moduleName}`, error);
                logModuleError(moduleName, 'Navigation Error', error, { action: 'menu' });
                // Fallback only after logging
                window.location.href = '/map';
            }
        }
    };
};

// Universal navigation handler with error recovery (LEGACY VERSION - kept for backward compatibility)
export const createNavigationHandlerLegacy = (navigate, level, chapter, moduleName, modulesOrder = null) => {
    // Use provided modules order or fallback to default
    const moduleSequence = modulesOrder || ['dialogue', 'vocabulary', 'translation', 'grammar', 'cloze', 'shapePuzzle', 'complete'];

    return {
        // Navigate to next module in sequence
        next: () => {
            console.log(`🔄 ${moduleName}: Navigating to next module`);
            console.log(`📋 Using modules order:`, moduleSequence);
            try {
                const currentIndex = moduleSequence.indexOf(moduleName);
                const nextModule = moduleSequence[currentIndex + 1];

                if (nextModule) {
                    navigate(`/level/${level}/chapter/${chapter}/${nextModule}`);
                } else {
                    // Last module, go to completion
                    navigate(`/level/${level}/chapter/${chapter}/complete`);
                }
            } catch (error) {
                logModuleError(moduleName, 'Navigation Error', error, { action: 'next' });
                navigate(`/level/${level}`);
            }
        },

        // Navigate to previous module
        previous: () => {
            console.log(`🔄 ${moduleName}: Navigating to previous module`);
            console.log(`📋 Using modules order:`, moduleSequence);
            try {
                const currentIndex = moduleSequence.indexOf(moduleName);
                const prevModule = moduleSequence[currentIndex - 1];

                if (prevModule) {
                    navigate(`/level/${level}/chapter/${chapter}/${prevModule}`);
                } else {
                    // First module, go to level page
                    navigate(`/level/${level}`);
                }
            } catch (error) {
                logModuleError(moduleName, 'Navigation Error', error, { action: 'previous' });
                navigate(`/level/${level}`);
            }
        },

        // Navigate to menu
        menu: () => {
            console.log(`🔄 ${moduleName}: Navigating to menu`);
            try {
                navigate('/map');
            } catch (error) {
                logModuleError(moduleName, 'Navigation Error', error, { action: 'menu' });
                window.location.href = '/map';
            }
        }
    };
};
